import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import {
  defaultOdd,
  FutureOddModel,
  FutureResponseModel,
  GameResponseModel,
  OddModel,
  EventModel,
  OddsMessage,
} from 'models';
import { AssetResponseModel, Coin } from 'models/asset';
import { WalletResponseModel } from 'models/wallet';

export interface BetModel {
  odd: OddModel;
  homeTeam: string;
  awayTeam: string;
  category: string;
  gameId: string;
  singleAmount: number;
  homeTeamLogo: string;
  awayTeamLogo: string;
  gameStatus: string;
}

export interface FutureBetModel {
  odd: FutureOddModel;
  team: string;
  category: string;
  futureId: string;
  league: string;
  singleAmount: number;
}

export interface SearchModel {
  searchText: string;
  isSearchLayout: boolean;
}

export interface NewsState {
  showAuthPage: number;
  isShowBetForm: boolean;
  isShowSubMenu: boolean;
  isShowCashierMenu: boolean;
  isShowHomeMenu: boolean;
  isShowTransactionModal: boolean;
  games: GameResponseModel[] | undefined;
  oldGames: GameResponseModel[] | undefined;
  bets: BetModel[];
  currentConverter: string;
  coins: Coin[];
  assets: AssetResponseModel[];
  balance: string;
  transferBalance: string;
  currentTurbo: string;
  fetchMyBet: boolean;
  search: SearchModel;
  isShowMfaModal: boolean;
  isShowChatSubMenu: boolean;
  futures: FutureResponseModel[] | undefined;
  futureBets: FutureBetModel[];
  currentCurrency: string;
  currentWallet: WalletResponseModel | undefined;
  userWallets: WalletResponseModel[];
  currentAmount: string;
  showEventNoti: boolean;
  event: EventModel | null;
  cashierMenuTab: number | null;
  showConfetti: boolean;
}
const initialState: NewsState = {
  showAuthPage: -1,
  isShowBetForm: false,
  isShowSubMenu: false,
  isShowCashierMenu: false,
  isShowHomeMenu: false,
  isShowTransactionModal: false,
  games: undefined,
  oldGames: undefined,
  bets: [],
  currentConverter: 'american',
  coins: [],
  assets: [],
  balance: '0',
  transferBalance: '0',
  currentTurbo: 'none',
  fetchMyBet: false,
  search: {
    searchText: '',
    isSearchLayout: false,
  },
  isShowMfaModal: false,
  isShowChatSubMenu: false,
  futures: undefined,
  futureBets: [],
  // currentCurrency: getLocalStorage('currentCurrency') || '$TBET',
  currentCurrency: '$TBET',
  currentWallet: undefined,
  userWallets: [],
  currentAmount: '50',
  showEventNoti: false,
  event: null,
  cashierMenuTab: null,
  showConfetti: false,
};

const homeSlice = createSlice({
  name: 'home',
  initialState,
  reducers: {
    setShowBetForm: (state, action: PayloadAction<boolean>) => {
      state.isShowBetForm = action.payload;
    },
    setShowSubMenu: (state, action: PayloadAction<boolean>) => {
      state.isShowSubMenu = action.payload;
    },
    setShowCashierMenu: (state, action: PayloadAction<boolean>) => {
      state.isShowCashierMenu = action.payload;
    },
    setShowHomeMenu: (state, action: PayloadAction<boolean>) => {
      state.isShowHomeMenu = action.payload;
    },
    setGames: (state, action: PayloadAction<GameResponseModel[] | undefined>) => {
      state.games = action.payload;
    },
    setOldGames: (state, action: PayloadAction<GameResponseModel[] | undefined>) => {
      state.oldGames = action.payload;
    },
    setBets: (state, action: PayloadAction<BetModel[]>) => {
      state.bets = action.payload;
    },
    addBet: (state, action: PayloadAction<BetModel>) => {
      state.bets.push(action.payload);
    },
    removeBet: (state, action: PayloadAction<string>) => {
      state.bets = state.bets.filter((bet) => bet.odd.id !== action.payload);
    },
    removeAllBets: (state) => {
      state.bets = [];
    },
    setCurrentConverter: (state, action: PayloadAction<string>) => {
      state.currentConverter = action.payload;
    },
    setCoins: (state, action: PayloadAction<Coin[]>) => {
      state.coins = action.payload;
    },
    setAssets: (state, action: PayloadAction<AssetResponseModel[]>) => {
      state.assets = action.payload;
    },
    setBlance: (state, action: PayloadAction<string>) => {
      state.balance = action.payload;
    },
    setTransferBalance: (state, action: PayloadAction<string>) => {
      state.transferBalance = action.payload;
    },
    changeSingleAmount: (state, action: PayloadAction<{ id: string; amount: number }>) => {
      const index = state.bets.findIndex((bet) => bet.odd.id === action.payload.id);
      if (index !== -1) state.bets[index].singleAmount = action.payload.amount;
    },
    changeSingleFutureAmount: (state, action: PayloadAction<{ id: string; amount: number }>) => {
      const index = state.futureBets.findIndex((bet) => bet.odd.id === action.payload.id);
      if (index !== -1) state.futureBets[index].singleAmount = action.payload.amount;
    },
    changeBetOdds: (state, action: PayloadAction<OddModel[]>) => {
      state.bets = state.bets
        .filter((bet) => action.payload.some((odd) => odd.id === bet.odd.id))
        .map((bet) => ({
          ...bet,
          odd: action.payload.find((o) => o.id === bet.odd.id) || defaultOdd,
        }));
    },
    setCurrentTurbo: (state, action: PayloadAction<string>) => {
      state.currentTurbo = action.payload;
    },
    setFetchMyBet: (state, action: PayloadAction<boolean>) => {
      state.fetchMyBet = action.payload;
    },
    setSearch: (state, action: PayloadAction<SearchModel>) => {
      state.search = action.payload;
    },
    onChangeAuthPage: (state, action: PayloadAction<number>) => {
      state.showAuthPage = action.payload;
    },
    setIsShowTransactionModal: (state, action: PayloadAction<boolean>) => {
      state.isShowTransactionModal = action.payload;
    },
    setIsShowMfaModal: (state, action: PayloadAction<boolean>) => {
      state.isShowMfaModal = action.payload;
    },
    setIsShowChatSubMenu: (state, action: PayloadAction<boolean>) => {
      state.isShowChatSubMenu = action.payload;
    },
    setFutures: (state, action: PayloadAction<FutureResponseModel[] | undefined>) => {
      state.futures = action.payload;
    },
    setFutureBets: (state, action: PayloadAction<FutureBetModel[]>) => {
      state.futureBets = action.payload;
    },
    addFutureBet: (state, action: PayloadAction<FutureBetModel>) => {
      state.futureBets.push(action.payload);
    },
    removeFutureBet: (state, action: PayloadAction<string>) => {
      state.futureBets = state.futureBets.filter((bet) => bet.odd.id !== action.payload);
    },
    removeAllFutureBets: (state) => {
      state.futureBets = [];
    },
    setCurrentCurrency: (state, action: PayloadAction<string>) => {
      state.currentCurrency = action.payload;
    },
    setCurrentWallet: (state, action: PayloadAction<WalletResponseModel>) => {
      state.currentWallet = action.payload;
    },
    setUserWallets: (state, action: PayloadAction<WalletResponseModel[]>) => {
      state.userWallets = action.payload;
    },
    setCurrentAmount: (state, action: PayloadAction<string>) => {
      state.currentAmount = action.payload;
    },
    closeAllModal: (state) => {
      state.isShowCashierMenu = false;
      state.isShowBetForm = false;
    },
    setShowEventNoti: (state, action: PayloadAction<boolean>) => {
      state.showEventNoti = action.payload;
    },
    setEvent: (state, action: PayloadAction<EventModel[]>) => {
      state.event = action.payload[0];
    },
    setCashierMenuTab: (state, action: PayloadAction<number | null>) => {
      state.cashierMenuTab = action.payload;
    },
    setShowConfetti: (state, action: PayloadAction<boolean>) => {
      state.showConfetti = action.payload;
    },
    updateBet: (state, action: PayloadAction<OddModel[]>) => {
      const updatedOddsMap = new Map(action.payload.map((odd) => [odd.id, odd]));
      state.bets = state.bets.map((bet) => {
        const updatedOdd = updatedOddsMap.get(bet.odd.id);
        if (updatedOdd) {
          return {
            ...bet,
            odd: updatedOdd,
          };
        }
        return bet;
      });
    },
  },
  extraReducers: {},
});

// NOTE: Actions
export const {
  setShowBetForm,
  setShowSubMenu,
  setShowHomeMenu,
  setGames,
  setOldGames,
  addBet,
  removeAllBets,
  removeBet,
  setBets,
  setShowCashierMenu,
  setCurrentConverter,
  setCoins,
  setAssets,
  setBlance,
  changeSingleAmount,
  changeBetOdds,
  setCurrentTurbo,
  setFetchMyBet,
  setSearch,
  onChangeAuthPage,
  setIsShowTransactionModal,
  setTransferBalance,
  setIsShowMfaModal,
  setIsShowChatSubMenu,
  setFutures,
  setFutureBets,
  addFutureBet,
  removeFutureBet,
  removeAllFutureBets,
  changeSingleFutureAmount,
  setCurrentCurrency,
  setCurrentWallet,
  setUserWallets,
  setCurrentAmount,
  closeAllModal,
  setShowEventNoti,
  setEvent,
  setCashierMenuTab,
  setShowConfetti,
  updateBet,
} = homeSlice.actions;

// NOTE: Selectors
export const selectIsShowBetForm = (state: RootState) => state.home.isShowBetForm;
export const selectIsShowSubMenu = (state: RootState) => state.home.isShowSubMenu;
export const selectGames = (state: RootState) => state.home.games;
export const selectOldGames = (state: RootState) => state.home.oldGames;
export const selectBets = (state: RootState) => state.home.bets;
export const selectIsShowCashierMenu = (state: RootState) => state.home.isShowCashierMenu;
export const selectCurrentConverter = (state: RootState) => state.home.currentConverter;
export const selectCoins = (state: RootState) => state.home.coins;
export const selectAssets = (state: RootState) => state.home.assets;
export const selectBalance = (state: RootState) => state.home.balance;
export const selectIsShowHomeMenu = (state: RootState) => state.home.isShowHomeMenu;
export const selectCurrentTurbo = (state: RootState) => state.home.currentTurbo;
export const selectFetchMyBet = (state: RootState) => state.home.fetchMyBet;
export const selectSearch = (state: RootState) => state.home.search;
export const selectShowAuthPage = (state: RootState) => state.home.showAuthPage;
export const selectIsShowTransactionModal = (state: RootState) => state.home.isShowTransactionModal;
export const selectTransferBalance = (state: RootState) => state.home.transferBalance;
export const selectIsShowMfaModal = (state: RootState) => state.home.isShowMfaModal;
export const selectIsShowChatSubMenu = (state: RootState) => state.home.isShowChatSubMenu;
export const selectFutures = (state: RootState) => state.home.futures;
export const selectFutureBets = (state: RootState) => state.home.futureBets;
export const selectCurrentCurrency = (state: RootState) => state.home.currentCurrency;
export const selectCurrentWallet = (state: RootState) => state.home.currentWallet;
export const selectUserWallets = (state: RootState) => state.home.userWallets;
export const selectCurrentAmount = (state: RootState) => state.home.currentAmount;
export const selectShowEventNoti = (state: RootState) => state.home.showEventNoti;
export const selectEvent = (state: RootState) => state.home.event;
export const selectCashierMenuTab = (state: RootState) => state.home.cashierMenuTab;
export const selectShowConfetti = (state: RootState) => state.home.showConfetti;
// NOTE: Reducer
export default homeSlice.reducer;
